import type { HTMLAttributes, ReactNode } from "react";
import type { TSupportedLocale } from "libs/i18n/src/lib/i18n.locales";
import type {
  TNewsletterSignupProps,
} from "@/ui/components/sections/marketing/NewsletterSignUp";
import type { TIconKey } from "@/ui/components/primitives/Icon";
import { cn, type DefinedDefaults, type TWithDataComponent } from "@/ui/lib";

/*-------------------------------------
|  Constants
|-------------------------------------*/

export const FOOTER_MAIN_CONTAINER_CLASS_NAME = cn(
  "relative flex w-fit flex-col gap-4 px-3 pb-4 pt-8",
  "md:gap-6 md:px-0 md:pb-8 md:pt-6",
  "xl:gap-12 xl:pb-12 xl:pt-16",
);

export const FOOTER_PRIMARY_NAVIGATION_LIST_CLASS_NAME = cn(
  "grid grid-cols-2",
  "md:w-[598px] md:grid-cols-4 md:gap-x-6",
  "xl:w-[1032px] xl:gap-x-12",
);

export const FOOTER_HANGING_CAT_CLASS_NAME =
  "pointer-events-none absolute right-0 top-0 z-0 hidden w-24 md:block";

export const FOOTER_SECONDARY_TEXT_CLASS_NAME =
  "text-sys-base leading-sys-1-3 text-sys-neutral-600 xl:text-sys-lg";

export const FOOTER_COUNTRY_TOGGLE_WRAPPER_CLASS_NAME = "mx-auto w-full max-w-[342px] px-3 md:px-0";

export const FOOTER_COUNTRY_TOGGLE_BUTTON_CLASS_NAME =
  "flex h-[52px] w-full items-center gap-4 rounded-[26px] bg-white px-[15px] text-left text-foreground";

export const FOOTER_COUNTRY_OPTION_CLASS_NAME =
  "flex cursor-pointer items-center gap-4 rounded-[10px] border-solid p-4 text-foreground transition-colors";

export const FOOTER_COUNTRY_OPTION_STATE_CLASS_MAP = {
  selected: "border-[1.5px] border-sys-black-850 bg-[#f4f5ed]",
  unselected: "border border-[#d1d1cc] bg-white",
} as const;

/*-------------------------------------
|  Types
|-------------------------------------*/

export type TFooterNavigationLink = {
  id: string;
  label: string;
  href: string;
  icon?: TIconKey;
  ariaLabel?: string;
};

export type TFooterNavigationSection = {
  id: string;
  title: string;
  links: TFooterNavigationLink[];
};

export type TFooterCountryOption = {
  icon: TIconKey;
  currency: string;
};

export type TFooterCountryToggleProps = {
  /** Defaults to setting the region cookie and reloading, for apps without a locale URL prefix. */
  onCountryChange?: (locale: TSupportedLocale) => void;
  className?: string;
};

export type TFooterCountrySelectDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentLocale: TSupportedLocale;
  onConfirm: (locale: TSupportedLocale) => void;
};

export type TFooterProps = Omit<HTMLAttributes<HTMLElement>, "children"> &
  TWithDataComponent & {
    primaryNavigation: TFooterNavigationSection[];
    secondaryNavigation: TFooterNavigationLink[];
    socialNavigation: TFooterNavigationLink[];
    newsletter?: TNewsletterSignupProps;
    isSignedIn?: boolean;
    countryToggle?: ReactNode;
  };

type TFooterDefaultProps = DefinedDefaults<
  TFooterProps,
  "data-component"
>;

/*-------------------------------------
|  Defaults
|-------------------------------------*/

// Copy falls back to the Components.Marketing.NewsletterSignUp translations
export const FOOTER_DEFAULT_NEWSLETTER_PROPS: TNewsletterSignupProps = {
  input: {
    name: "newsletter_email",
    type: "email",
    className: "text-foreground",
  },
};

export const FOOTER_COUNTRY_OPTIONS: Record<TSupportedLocale, TFooterCountryOption> = {
  de: {
    icon: "flagGermany",
    currency: "€ - EUR",
  },
  en: {
    icon: "flagUnitedKingdom",
    currency: "£ - GBP",
  },
};

export const FOOTER_DEFAULTS = {
  "data-component": "Footer",
} satisfies TFooterDefaultProps;
